/**
 * PWA install-prompt: vangt `beforeinstallprompt` (Chromium) én toont een
 * iOS-handleiding-dialog (Safari heeft geen native prompt).
 * - Verschijnt maximaal 1 keer per sessie, dismissed-flag in localStorage.
 * - Geen storende herhaling; kan later via `openPwaInstallDialog()` opnieuw.
 */
import { useEffect, useState, useCallback } from "react";
import { X, Share, Plus, Smartphone } from "lucide-react";
import { isIos, isStandalone } from "@/lib/pwa";

const DISMISS_KEY = "np_pwa_dismissed_at";

export default function PwaInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  const shouldShow = useCallback(() => {
    if (isStandalone()) return false;
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    // Toon niet opnieuw binnen 7 dagen na dismiss
    return !last || Date.now() - last > 7 * 24 * 60 * 60 * 1000;
  }, []);

  useEffect(() => {
    if (!shouldShow()) return;
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS: geen event, toon banner na 3s met handleiding
    if (isIos()) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [shouldShow]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (isIos()) { setIosHelp(true); return; }
    if (!deferred) { dismiss(); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    setVisible(false);
    if (outcome !== "accepted") localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!visible) return null;

  return (
    <>
      <div
        className="fixed bottom-20 lg:bottom-4 inset-x-3 z-50 rounded-lg border border-neutral-200 bg-white shadow-lg p-3 flex items-center gap-3 max-w-md mx-auto"
        role="dialog"
        aria-label="App installeren"
        data-testid="pwa-install-banner"
      >
        <Smartphone className="h-5 w-5 text-[color:var(--sage)] shrink-0" />
        <div className="flex-1 text-xs text-neutral-800 leading-tight">
          <div className="font-semibold">App installeren</div>
          <div className="text-neutral-600">Zet Webster op je beginscherm voor snelle toegang.</div>
        </div>
        <button
          onClick={install}
          data-testid="pwa-install-btn"
          className="text-xs font-semibold text-white bg-[color:var(--sage)] px-3 py-1.5 rounded-md"
        >
          {isIos() ? "Uitleg" : "Installeren"}
        </button>
        <button onClick={dismiss} className="p-1 text-neutral-400 hover:text-neutral-600" aria-label="Sluiten" data-testid="pwa-install-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      {iosHelp && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" role="dialog" aria-label="Installatie-uitleg iOS" data-testid="pwa-ios-help">
          <div className="bg-white rounded-lg p-4 max-w-md w-full">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg">Zet op beginscherm</h3>
              <button onClick={() => { setIosHelp(false); dismiss(); }} className="p-1" aria-label="Sluiten">
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="space-y-2 text-sm text-neutral-700">
              <li className="flex gap-2"><span className="font-bold text-[color:var(--sage)]">1.</span> Tik op <Share className="inline h-4 w-4" /> onderin Safari.</li>
              <li className="flex gap-2"><span className="font-bold text-[color:var(--sage)]">2.</span> Kies <strong>Zet op beginscherm</strong> <Plus className="inline h-4 w-4" />.</li>
              <li className="flex gap-2"><span className="font-bold text-[color:var(--sage)]">3.</span> Bevestig met <strong>Voeg toe</strong>.</li>
            </ol>
            <button
              onClick={() => { setIosHelp(false); dismiss(); }}
              className="mt-4 w-full text-sm font-semibold text-white bg-[color:var(--sage)] py-2 rounded-md"
              data-testid="pwa-ios-close"
            >Ok, ik heb het</button>
          </div>
        </div>
      )}
    </>
  );
}
